// Belt placement, connectivity and flow (PLAN §3.3).

import { RULES } from "../rules";
import {
  type Color,
  fileOf,
  pieceColor,
  rankOf,
  ROOK_DIRS,
  type Side,
  square
} from "../board";
import { hasCell, pieceAt, type Position } from "../position";
import { materialLinks } from "./adjacency";
import { type BeltPlacementError, beltPlacementError } from "./beltParts";
import {
  beltDirAt,
  beltOnAt,
  beltOwnerAt,
  blueprintOwnerAt,
  cargoTotal,
  clearBelt,
  clearCargo,
  DIR_OFFSET,
  type Direction,
  hasBelt,
  hasCargo,
  hasStructure,
  type IndustryState,
  liveSquares,
  NO_BELT,
  pushUnit,
  removeUnit,
  setBelt,
  setBeltDir,
  unitAt,
  unitCount,
  unitGrade,
} from "./state";

export function beltCount(industry: IndustryState, owner: Color): number {
  let count = 0;
  for (const sq of liveSquares(industry.beltDir)) {
    if (beltOwnerAt(industry, sq) === owner) count++;
  }
  return count;
}

/** Parts drawn and still waiting for their iron — what the shelf owes. */
export function blueprintCount(industry: IndustryState, owner: Color): number {
  let count = 0;
  for (const sq of liveSquares(industry.blueprintDir)) {
    if (blueprintOwnerAt(industry, sq) === owner) count++;
  }
  return count;
}

/** Why a part may not go down (§31 S2) — `beltPlacementError`'s words. */
export type PlacementError = BeltPlacementError;

/**
 * Chebyshev distance to the nearest piece of `owner`, or Infinity if they have
 * none. King-move distance rather than Manhattan: it matches how presence
 * reads on a board, where a piece dominates the ring of squares around it
 * equally rather than reaching further along ranks and files.
 */
export function distanceToArmy(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Side
): number {
  return reach(position, industry, sq).mine[owner] ?? Infinity;
}

/**
 * How near this square is to each side, counting **buildings as well as
 * pieces**.
 *
 * A structure projects territory (PLAN §14.3.4), and it has to: without it a
 * forward warehouse does nothing to help you build the lane that serves it,
 * which reads as the building being inert. The alternative is defensible too,
 * which is exactly why it is written down rather than left to whichever caller
 * was implemented first.
 *
 * One function because there were two copies of this loop — belt placement had
 * one and structure placement the other — and two copies of "whose ground is
 * this" is how the two rules drift apart.
 */
export function reach(
  position: Position,
  industry: IndustryState,
  sq: number
): { mine: Record<number, number>; nearest: number } {
  const mine: Record<number, number> = {};
  const note = (other: number, side: Side): void => {
    const distance = Math.max(
      Math.abs(fileOf(other) - fileOf(sq)),
      Math.abs(rankOf(other) - rankOf(sq))
    );
    if (mine[side] === undefined || distance < mine[side]) mine[side] = distance;
  };

  for (const [other, piece] of position.board) note(other, pieceColor(piece));
  for (const [other, structure] of industry.structures) note(other, structure.owner);

  let nearest = Infinity;
  for (const distance of Object.values(mine)) nearest = Math.min(nearest, distance);
  return { mine, nearest };
}

/**
 * Ground this side may build on: closer to it than anybody else, ties to them.
 *
 * The one rule for what you may build on, whether it is a belt part or a
 * building — two would be two things for a player to learn about one idea.
 */
export function holdsGround(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Side
): boolean {
  const { mine } = reach(position, industry, sq);
  const ours = mine[owner] ?? Infinity;
  let nearestRival = Infinity;
  for (const [side, distance] of Object.entries(mine)) {
    if (Number(side) === owner) continue;
    if (distance < nearestRival) nearestRival = distance;
  }
  return ours <= nearestRival;
}

/**
 * May `owner` lay a part on `sq` now? (§31 S2) A part is a structure: built at
 * once, beside a piece of theirs, for `BELT_BILL` — `beltPlacementError`.
 * Territory (`holdsGround`) no longer gates track.
 */
export function canPlaceBelt(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): PlacementError | null {
  return beltPlacementError(position, industry, sq, owner);
}

/**
 * Whether a *run* may cover this square — placement, or a re-point of a part
 * you already own. Drawing over your own track re-aims it (free, §31 Q5)
 * rather than leaving a hole where two lanes meet; a re-point asks nothing
 * about funds or adjacency, for the reason `rotate-belt` does not.
 */
export function canExtendBelt(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): PlacementError | null {
  if (hasBelt(industry, sq) && beltOwnerAt(industry, sq) === owner) return null;
  return canPlaceBelt(position, industry, sq, owner);
}

/**
 * Turn a part you already own to `dir`, and say whether there was one to turn.
 * Free (§31 Q5). Deliberately not `placeBelt`, which clears the square's
 * cargo: re-aiming has never cost the cargo.
 */
export function repointBelt(
  industry: IndustryState,
  sq: number,
  owner: Color,
  dir: Direction
): boolean {
  if (hasBelt(industry, sq) && beltOwnerAt(industry, sq) === owner) {
    setBeltDir(industry, sq, dir);
    return true;
  }
  return false;
}

/** A run's squares, and where each of its parts points. */
export interface BeltRun {
  squares: number[];
  directions: Direction[];
}

/**
 * Every way a run gets from `from` to `to` turning **at most once**.
 *
 * **A run may be any shape, so long as it feeds through itself.** That is the
 * whole of the rule: consecutive squares are orthogonal neighbours, so every
 * part hands on to the next one. It used to be ranks and files only, which was
 * never a rule about belts — a part carries along `DIR_OFFSET`, and a bend is
 * two parts that connect exactly as well as two straight ones.
 *
 * **Where a part points is derived, not chosen**: each aims at its successor
 * and the last inherits the one before it. That is what puts the corner on the
 * *second* leg by construction — getting it the other way round makes the
 * turning part carry cargo straight past the bend and tip it into open ground.
 *
 * Two routes when the ends share neither rank nor file: along the rank and then
 * the file, or the other way about. Which is buildable depends entirely on what
 * is standing where, so the choice belongs to `bestBeltRoute`, which can see
 * the board — this is only the geometry. One route when they are aligned.
 *
 * **None when they are the same square**: a run of one part has no direction to
 * derive from its ends, and the caller wants `lay-belt` with an explicit one.
 *
 * The squares include both ends, ordered from `from` outward, which is the
 * order the reducer lays them in and therefore the order a partial run stops
 * in.
 */
export function beltRoutes(from: number, to: number): BeltRun[] {
  if (from === to) return [];

  const df = fileOf(to) - fileOf(from);
  const dr = rankOf(to) - rankOf(from);

  const walk = (start: number, end: number, out: number[]): void => {
    const step = DIR_OFFSET[
      rankOf(end) > rankOf(start)
        ? 0
        : fileOf(end) > fileOf(start)
          ? 1
          : rankOf(end) < rankOf(start)
            ? 2
            : 3
    ];
    for (let sq = start; sq !== end; sq += step) out.push(sq);
    out.push(end);
  };

  const run = (squares: number[]): BeltRun | null => {
    const directions = beltPathDirections(squares);
    return directions ? { squares, directions } : null;
  };

  if (df === 0 || dr === 0) {
    const squares: number[] = [];
    walk(from, to, squares);
    const only = run(squares);
    return only ? [only] : [];
  }

  // The rank first, then the file; and the file first, then the rank. The
  // corner square belongs to whichever leg arrives at it — it is written once
  // and `beltPathDirections` aims it at what comes next.
  const corners = [square(fileOf(to), rankOf(from)), square(fileOf(from), rankOf(to))];
  const out: BeltRun[] = [];
  for (const corner of corners) {
    const squares: number[] = [];
    walk(from, corner, squares);
    squares.pop();
    walk(corner, to, squares);
    const bent = run(squares);
    if (bent) out.push(bent);
  }
  return out;
}

/**
 * The route between two squares this side can lay most of, or null.
 *
 * **The corner is chosen by the board, not by the geometry.** A bend can go two
 * ways round and which one is buildable depends entirely on what is standing
 * where, so both are measured and the one that reaches furthest wins. Ties keep
 * the first, which is a fixed order — two clients derive this independently
 * (PLAN §5.5).
 *
 * One function because the reducer lays this and the preview draws it, and a
 * preview that chose the other corner would be a promise the board does not
 * keep.
 */
/**
 * Would this run's head end up pointing at a part of ours that points back?
 *
 * **The smallest ring there is, and the one nothing else catches.** Two parts
 * facing each other are fed by each other, so `laneIsOrphan` is false for both
 * forever and no demolition rule will ever touch them; they deliver nothing, so
 * the cargo shuttles between them until somebody re-points one by hand. The
 * planner already refuses to *rotate* into one (`feedsUs`); this is the same
 * refusal at the other door, where a run is being laid.
 *
 * Read off the run's own last direction rather than recomputed, so it cannot
 * disagree with what `beltPathDirections` will actually build.
 */
function endsOpposed(
  industry: IndustryState,
  route: BeltRun,
  owner: Color
): boolean {
  const last = route.squares[route.squares.length - 1];
  const facing = route.directions[route.directions.length - 1];
  if (last === undefined || facing === undefined) return false;
  const beyond = last + DIR_OFFSET[facing];
  if (!hasBelt(industry, beyond) || beltOwnerAt(industry, beyond) !== owner) return false;
  const theirs = beltDirAt(industry, beyond);
  return theirs !== -1 && beyond + DIR_OFFSET[theirs] === last;
}

export function bestBeltRoute(
  position: Position,
  industry: IndustryState,
  from: number,
  to: number,
  owner: Color
): BeltRun | null {
  let best: BeltRun | null = null;
  let reach = -1;
  let bestOpposed = true;
  for (const route of beltRoutes(from, to)) {
    let far = 0;
    for (const sq of route.squares) {
      if (canExtendBelt(position, industry, sq, owner)) break;
      far++;
    }
    // **Reach first, opposition as the tie-break.** How much of the run can
    // actually be built is what this function is for, and a route that gets
    // material further is worth more than one that merely ends tidily. Where
    // the two routes build equally far — which is the ordinary case for a bend,
    // since both ways round cover the same squares — the one that does not end
    // nose to nose with our own track wins. It replaced "ties to the first",
    // which is a coin toss dressed as a rule.
    const opposed = endsOpposed(industry, route, owner);
    if (far > reach || (far === reach && bestOpposed && !opposed)) {
      reach = far;
      best = route;
      bestOpposed = opposed;
    }
  }
  return best;
}

/**
 * The direction each square of a freehand path should face: at its successor,
 * with the last part inheriting the one before it.
 *
 * A drawn path can turn corners, which a straight run cannot, so each part is
 * aimed individually rather than sharing one direction. Returns null if any
 * step is not to an orthogonal neighbour — a belt carries along DIR_OFFSET,
 * and a path that skips or cuts a corner would not actually connect.
 */
export function beltPathDirections(squares: readonly number[]): Direction[] | null {
  if (squares.length === 0) return null;
  const dirs: Direction[] = [];

  for (let i = 0; i < squares.length - 1; i++) {
    const step = squares[i + 1] - squares[i];
    const dir = DIR_OFFSET.indexOf(step);
    if (dir < 0) return null;
    dirs.push(dir as Direction);
  }

  // The head has nothing to point at, so it carries on the way it came. A
  // single-square path has nothing at all to go on and is not a path.
  if (dirs.length === 0) return null;
  dirs.push(dirs[dirs.length - 1]);
  return dirs;
}

export function placeBelt(
  industry: IndustryState,
  sq: number,
  owner: Color,
  dir: Direction
): void {
  setBelt(industry, sq, dir, owner);
  clearCargo(industry, sq);
}

/**
 * Remove a part. Returns everything it was holding, so callers can spill it —
 * a merged pile spills as many units as it had accumulated, not one.
 */
/**
 * Lift a part, and hand back what was riding it — as *units*, not a count.
 *
 * It used to return how many and the caller turned that into raw scrap, which
 * meant taking up a lane melted everything on it back to grade 0. A player
 * straightening out a junction lost the whole afternoon's refining, and the
 * remove tool became something to be frightened of rather than something to
 * tidy up with.
 *
 * Crushing still spills as scrap — grade is destroyed with its carrier, and an
 * enemy standing on your lane is a carrier being destroyed. Lifting your own
 * part is not.
 */
export function removeBelt(industry: IndustryState, sq: number): number[] {
  const carried: number[] = [];
  for (let i = unitCount(industry, sq, "cargo") - 1; i >= 0; i--) {
    carried.push(unitAt(industry, sq, i, "cargo"));
  }
  clearCargo(industry, sq);
  clearBelt(industry, sq);
  return carried.reverse();
}

/**
 * Which of a player's parts are connected, through their own network, to one
 * of their pieces. An orphaned network goes inert rather than being destroyed,
 * so walking a piece back reconnects it.
 */
export function liveBelts(
  position: Position,
  industry: IndustryState,
  owner: Color
): Set<number> {
  const live = new Set<number>();
  const queue: number[] = [];

  for (const sq of liveSquares(industry.beltDir)) {
    if (beltOwnerAt(industry, sq) !== owner) continue;
    if (!beltIsFed(position, industry, sq, owner)) continue;
    live.add(sq);
    queue.push(sq);
  }

  while (queue.length > 0) {
    const sq = queue.pop() as number;
    for (const adj of ownPartsBeside(position, industry, sq, owner)) {
      if (live.has(adj)) continue;
      live.add(adj);
      queue.push(adj);
    }
  }

  return live;
}

/**
 * Is this part fed *directly* — a piece of the owner's touching it?
 *
 * The seed half of `liveBelts`, named because `beltIsRunning` needs the same
 * sentence and two readings of "what starts a network" is how one rule becomes
 * two. Including the diagonal, when the piece there is one that handles
 * material diagonally.
 */
function beltIsFed(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): boolean {
  for (const adj of materialLinks(position, industry, sq)) {
    const piece = pieceAt(position, adj);
    if (piece && pieceColor(piece) === owner) return true;
  }
  return false;
}

/** The spread half of `liveBelts`: the owner's parts orthogonally beside `sq`. */
function ownPartsBeside(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): number[] {
  const out: number[] = [];
  for (const offset of ROOK_DIRS) {
    const adj = sq + offset;
    if (!hasCell(position, adj)) continue;
    if (beltDirAt(industry, adj) === NO_BELT || beltOwnerAt(industry, adj) !== owner) continue;
    out.push(adj);
  }
  return out;
}

/**
 * Is *this one part* running — the question `liveBelts` answers for all of
 * them, asked about one.
 *
 * **`liveBelts` is a sweep of the whole board**, and building the entire live
 * set to read one entry out of it made a per-move question sweep every belt
 * on the map — measured as turns visibly slowing down as a factory grew. This walks the
 * *component* `sq` belongs to instead, which is one lane, and stops the moment
 * it finds a feeder.
 *
 * The same rule, not a second one: it shares `beltIsFed` and `ownPartsBeside`
 * with `liveBelts`, so "what starts a network" and "how a network spreads" have
 * one definition each. `carry.test.ts` pins the two answers together, part by
 * part, over a board of fed, orphaned, lone, forked and enemy-owned runs.
 */
export function beltIsRunning(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): boolean {
  if (beltDirAt(industry, sq) === NO_BELT || beltOwnerAt(industry, sq) !== owner) return false;

  const seen = new Set<number>([sq]);
  const queue = [sq];
  while (queue.length > 0) {
    const at = queue.pop() as number;
    if (beltIsFed(position, industry, at, owner)) return true;
    for (const adj of ownPartsBeside(position, industry, at, owner)) {
      if (seen.has(adj)) continue;
      seen.add(adj);
      queue.push(adj);
    }
  }
  return false;
}

/**
 * One unit changing places, as the renderer needs to see it.
 *
 * **Reported rather than re-derived.** The flow pass already knows exactly
 * which unit went where — it is the thing computing it — and a second opinion
 * worked out from the board afterwards cannot tell a merge from an arrival,
 * or which of two identical bars of iron is which. The renderer used to guess
 * with `upstreamOf` and slide the whole pile as one lump.
 *
 * A unit is identified by *where it was standing*: its square and its index in
 * that square's pile. Units carry no id — they are packed words, and two bars
 * of iron of the same provenance are the same word — so position is the only
 * identity there is, and it is enough because a pile is an ordered list.
 *
 * An index of -1 means **the square's own hands** rather than a slot in a
 * belt pile: a machine's buffers and its oven are drawn as one box, so that is
 * where the ingot goes to or comes from. On a belt the index is the slot.
 *
 * Only *changes* are reported; a unit nobody mentions stayed where it was.
 */
export interface CargoMove {
  from: number;
  fromIndex: number;
  to: number;
  toIndex: number;
  /**
   * Which pass moved it.
   *
   * A turn's industry is four things in order — lanes advance, machines take
   * what they want off them, ovens cook, everything hands on — and they used
   * to land on screen as one instantaneous change. The tag is what lets the
   * renderer play them as four short phases, each skipped when it is empty.
   */
  phase: MovePhase;
}

/** The four passes of a turn's industry, in the order they happen. */
export type MovePhase = "flow" | "grab" | "cook" | "handoff";

export interface FlowResult {
  /** Units delivered into machines this tick, keyed by machine square. */
  delivered: { square: number; grade: number }[];
  moved: number;
  /** Where each unit that changed places went. See `CargoMove`. */
  moves: CargoMove[];
}



/**
 * Advance every lane one square, simultaneously.
 *
 * Implementation trap (PLAN §4, and it bit v1): a lane must shuffle forward by
 * exactly one square per tick, not let one unit ride the whole lane in a
 * single tick. That used to be enforced by resolving head-end backwards and
 * *blocking* a follower whose destination was occupied.
 *
 * Blocking is gone — lanes merge now — and merging broke that enforcement the
 * moment it landed: with the block removed, iteration order decided everything
 * and a follower collapsed into its leader instead of the lane flowing. So the
 * tick is computed from a snapshot of the pre-tick state and applied all at
 * once. Every part moves or does not move based on where things were at the
 * start of the tick, which makes order irrelevant and the one-square rule
 * exact.
 *
 * Merging then means what it should: two lanes *converging* on one square add
 * together, and a lane backing up behind a stalled head piles onto it rather
 * than deadlocking.
 *
 * `deliver` both tests and commits, and takes the whole packed unit rather
 * than its grade: a pile can hand several units to one machine in a tick, a
 * pure predicate would not see the buffer filling up as it went, and the
 * unit's provenance has to survive the hand-off.
 */
export function advanceBelts(
  position: Position,
  industry: IndustryState,
  owner: Color,
  deliver: (square: number, unit: number) => boolean
): FlowResult {
  const live = liveBelts(position, industry, owner);
  const result: FlowResult = { delivered: [], moved: 0, moves: [] };

  // Staged arrivals, so a part that ships this tick cannot also receive this
  // tick — that is the whole of the one-square-per-tick rule.
  //
  // Each staged unit remembers where it set off from, because that is what the
  // renderer animates and it cannot be worked out later: by the time the pushes
  // happen the source square has been cleared.
  const incoming = new Map<number, { unit: number; from: number; fromIndex: number }[]>();
  const shipped: number[] = [];
  // Piles that would move on, settled for room below (§33 I3).
  const bound: { sq: number; next: number }[] = [];

  // Sorted, not Map order: iteration has to be positional to stay identical
  // on both clients (PLAN §5.5).
  for (const sq of liveSquares(industry.beltDir)) {
    if (!live.has(sq) || beltOnAt(industry, sq) !== 1) continue;
    if (!hasCargo(industry, sq)) continue;

    const next = sq + DIR_OFFSET[beltDirAt(industry, sq)];
    // A building's square is not a cell — that is how it blocks movement — so
    // without naming it here a lane ending at a warehouse simply stops, and
    // the one thing a warehouse is for never happens.
    if (!hasCell(position, next) && !hasStructure(industry, next)) continue;

    // A machine at the end of the lane takes what it has room for, oldest unit
    // first; the rest stays on the part and tries again next tick. The whole
    // unit is handed over, not just its grade, so its provenance survives the
    // hop into the machine.
    //
    // **A piece standing on a part of ours** (§31 S2 — a part is a structure
    // and is stood on) takes delivery only if it is a machine that accepts;
    // otherwise the belt under it carries the pile on as if it were not there.
    const onTrack =
      pieceAt(position, next) !== 0 &&
      !hasStructure(industry, next) &&
      beltDirAt(industry, next) !== NO_BELT &&
      beltOwnerAt(industry, next) === owner &&
      beltOnAt(industry, next) === 1;
    if (pieceAt(position, next) !== 0 || hasStructure(industry, next)) {
      // `taken` counts off the *original* pile, so a unit's `fromIndex` is
      // where it stood before this tick started rather than where the shrinking
      // pile left it.
      let taken = 0;
      while (cargoTotal(industry, sq) > 0) {
        const unit = unitAt(industry, sq, 0, "cargo");
        if (!deliver(next, unit)) break;
        result.delivered.push({ square: next, grade: unitGrade(unit) });
        result.moves.push({ from: sq, fromIndex: taken, to: next, toIndex: -1, phase: "flow" });
        removeUnit(industry, sq, 0, "cargo");
        result.moved++;
        taken++;
      }
      // Whatever the machine would not take shuffles down the pile. It has not
      // left the square, but it has changed places within it — and a pile that
      // is two ingots shorter is drawn in a different shape, so this is a real
      // movement rather than bookkeeping.
      //
      // Only when the machine actually took something. A lane whose head is
      // refused is the commonest state a working lane is in, and reporting its
      // whole pile as moving to where it already was would put every stalled
      // lane on the board into the animation phase, every tick.
      if (taken > 0) {
        for (let i = 0; i < cargoTotal(industry, sq); i++) {
          result.moves.push({ from: sq, fromIndex: taken + i, to: sq, toIndex: i, phase: "flow" });
        }
      }
      if (taken > 0 || !onTrack || cargoTotal(industry, sq) === 0) continue;
    }

    // Onward only into another live, running part of ours. Whether that part
    // is loaded is irrelevant: it is moving too, and if it is not, the piles
    // merge.
    if (
      beltDirAt(industry, next) === NO_BELT ||
      beltOwnerAt(industry, next) !== owner ||
      beltOnAt(industry, next) !== 1
    ) {
      continue;
    }

    bound.push({ sq, next });
  }

  // **A part holds `RULES.beltCapacity`** (§33 I3), so a pile moves on whole
  // only if it fits: what stays on `next` (unless `next` ships too) plus what
  // lands there before it, in board order. A refused pile stays put, which
  // takes room from the pile behind it — so settle to a fixpoint; refusals
  // only grow, so it ends.
  const ships = new Set(bound.map((one) => one.sq));
  for (let changed = true; changed; ) {
    changed = false;
    const arriving = new Map<number, number>();
    for (const { sq, next } of bound) {
      if (!ships.has(sq)) continue;
      const stays = ships.has(next) ? 0 : cargoTotal(industry, next);
      const pile = cargoTotal(industry, sq);
      const landed = arriving.get(next) ?? 0;
      if (stays + landed + pile > RULES.beltCapacity) {
        ships.delete(sq);
        changed = true;
        break;
      }
      arriving.set(next, landed + pile);
    }
  }
  for (const { sq, next } of bound) {
    if (!ships.has(sq)) continue;
    const arrivals = incoming.get(next) ?? [];
    for (let i = 0; i < cargoTotal(industry, sq); i++) {
      arrivals.push({ unit: unitAt(industry, sq, i, "cargo"), from: sq, fromIndex: i });
    }
    incoming.set(next, arrivals);
    result.moved += cargoTotal(industry, sq);
    shipped.push(sq);
  }

  for (const sq of shipped) clearCargo(industry, sq);
  // Sorted, not Map order, for the same reason as above.
  for (const sq of liveSquares(incoming)) {
    const bound = incoming.get(sq);
    if (!bound) continue;
    for (const { unit, from, fromIndex } of bound) {
      // Read *before* the push, because that is the slot it lands in — and it
      // cannot be worked out beforehand: whether the square kept any of its own
      // cargo depends on whether it shipped too, which the sweep above decides.
      result.moves.push({
        from,
        fromIndex,
        to: sq,
        toIndex: cargoTotal(industry, sq),
        phase: "flow"
      });
      pushUnit(industry, sq, unit, "cargo");
    }
  }

  return result;
}

/**
 * How far to follow a lane before giving up on it.
 *
 * A ring of our own track would otherwise walk forever. Sixty-four is well past
 * any lane anybody has built and is the same bound `Weights.chaseLimit` gives
 * the opponent, which asks this same question about its own network.
 */
export const LANE_CHASE = 64;

/**
 * Does following this lane forward arrive at something that can take delivery?
 *
 * A piece or a building — anything that is not simply more track. **A ring
 * answers false**: it is running, it is fed, and it delivers nothing, which is
 * the honest answer and the one that stops cargo being routed into a loop.
 *
 * This is a *preference* input and never a gate. Nothing refuses to load a lane
 * that answers false, because a lane is built from its tail and arrives nowhere
 * until its last part goes down — refusing one would mean a player could not
 * lay track ahead of the machine it is meant to feed.
 */
export function laneDelivers(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color,
  limit = LANE_CHASE
): boolean {
  let at = sq;
  for (let step = 0; step < limit; step++) {
    const facing = beltDirAt(industry, at);
    if (facing === NO_BELT) return false;
    const next = at + DIR_OFFSET[facing];

    if (hasStructure(industry, next)) return true;
    if (pieceAt(position, next) !== 0) return true;
    // Onward only into more live track of ours; anything else is the end.
    if (beltDirAt(industry, next) === NO_BELT) return false;
    if (beltOwnerAt(industry, next) !== owner) return false;
    if (beltOnAt(industry, next) !== 1) return false;
    at = next;
  }
  return false;
}


